import { Id } from '@/infra/id'
import { Model, type ModelProps } from '@/infra/model'
import { observable, computed, action } from 'mobx'
import { unixEpoch } from '@/utils/date-util'
import { Subject } from 'rxjs'

import type { MessageContent } from './content'
import type { MessageStatus } from './message-status'
import type { MessageType } from './message-type'
import { User } from '../iam/users/User'
import { stripMinidown } from '@/minidown/strip'

/**
 * Properties for setting a message.
 */
export interface MessageProps
  extends Omit<
    ModelProps<Message>,
    'recipientStatuses' | 'sendOperationState'
  > {
  recipientStatuses?: MessageStatus[]
  sendOperationState?: SendOperationState
}

/**
 * Represents a message in a conversation.
 */
export class Message extends Model<Message> {
  static readonly none = new Message()

  id = Id.none

  /**
   * An ID specified by the client (that's us!), used for deduplicating messages
   * when retrying to send a message that failed.
   */
  cid = Id.none

  @observable accessor conversationId = Id.none
  @observable.deep accessor recipientStatuses: MessageStatus[] = []
  @observable accessor dateCreated = unixEpoch()
  @observable accessor dateModified = unixEpoch()
  @observable accessor messageType: MessageType = 'Message'
  @observable accessor sender = User.none
  @observable accessor sendOperationState: SendOperationState = 'None'

  /**
   * The message content.
   */
  @observable.ref accessor content: MessageContent = {
    type: 'Text',
    text: '',
  }

  /**
   * Subject used to push status changes.
   */
  private readonly _statusChanges = new Subject<MessageStatus>()

  /**
   * Status changes as an observable stream.
   */
  readonly statusChanges$ = this._statusChanges.asObservable()

  /**
   * The current status of the message for the current user.
   */
  @computed get currentUserStatus() {
    return this.recipientStatuses.find((s) => s.user.isCurrentUser) ?? null
  }

  /**
   * Returns the text content of the message, regardless of content type.
   */
  @computed get text() {
    const content = this.content
    switch (content.type) {
      case 'Text':
      case 'System':
      case 'ExternalReference':
        return content.text
      case 'Call':
        return 'Call started'
      default:
        return ''
    }
  }

  /**
   * Checks whether this message can be marked as read by the current user.
   */
  @computed get canMarkAsRead() {
    if (this.sender.isCurrentUser) {
      return false
    }

    if (this.currentUserStatus?.deliveryStatus === 'Read') {
      return false
    }

    return true
  }

  /**
   * The notification text without any formatting.
   */
  @computed get plainText() {
    return stripMinidown(this.text)
  }

  /**
   * Gets the status for a particular user.
   */
  statusFor(userId: Id) {
    const status = this.recipientStatuses.find((s) => s.user.id === userId)
    return status ?? null
  }

  /**
   * Marks the message as delivered for the specified user.
   */
  @action.bound
  markDelivered(userId: Id) {
    const status = this.statusFor(userId)
    if (
      status &&
      status.deliveryStatus !== 'Delivered' &&
      status.deliveryStatus !== 'Read'
    ) {
      status.deliveryStatus = 'Delivered'
      this._statusChanges.next(status)
    }
  }

  /**
   * Marks the message as read for the specified user.
   */
  @action.bound
  markRead(userId: Id) {
    const status = this.statusFor(userId)
    if (status && status.deliveryStatus !== 'Read') {
      status.deliveryStatus = 'Read'
      this._statusChanges.next(status)
    }
  }
}

/**
 * For messages sent by the current user on this client,
 * indicates the state of the send operation.
 */
export type SendOperationState = 'None' | 'Sending' | 'Failed'
